const active = new WeakMap();
const money = value => Number.isSafeInteger(value) && value >= 0;
const identifier = value => /^(?:[1-9]\d*)$/.test(String(value));

export function cartEventPayload({cart, additions, kind = 'product'}, {origin, routesRoot = '/', country = '', product = null, language = 'en'} = {}) {
  if (!cart || !/^[A-Z]{3}$/.test(cart.currency) || !['total_price', 'original_total_price', 'total_discount'].every(key => money(cart[key])) || !Array.isArray(cart.items) || !Array.isArray(additions) || !additions.length) return null;
  const added = additions.filter(item => identifier(item.variantId) && identifier(item.productId) && Number.isSafeInteger(item.quantity) && item.quantity > 0);
  if (added.length !== additions.length) return null;
  const actual = added.map(item => cart.items.find(line => String(line.variant_id) === String(item.variantId) && String(line.product_id) === String(item.productId)));
  if (actual.some(line => !line || !money(line.price) || typeof line.product_title !== 'string' || !line.product_title)) return null;
  const first = actual[0];
  const url = new URL(first.url || `${routesRoot}products/${first.handle}`, origin);
  if (url.origin !== origin || !url.pathname.includes('/products/')) return null;
  url.search = '';
  url.hash = '';
  url.searchParams.set('variant', String(first.variant_id));
  const image = first.featured_image?.url || first.image;
  const imageURL = image ? new URL(image, origin) : null;
  const format = amount => new Intl.NumberFormat(language || 'en', {style: 'currency', currency: cart.currency}).format(amount / 100);
  const metadata = product && String(product.id) === String(first.product_id) ? product : null;
  const variant = metadata?.variants?.find(item => String(item.id) === String(first.variant_id));
  const fields = ['id', 'product_id', 'variant_id', 'key', 'quantity', 'title', 'product_title', 'variant_title', 'handle', 'url', 'image', 'vendor', 'product_type', 'sku', 'price', 'original_price', 'final_price', 'line_price', 'original_line_price', 'final_line_price', 'total_discount', 'variant_options', 'options_with_values'];
  const items = cart.items.map(line => Object.fromEntries(fields.filter(key => line[key] !== undefined).map(key => [key, line[key]])));
  return {
    ProductID: first.product_id,
    VariantID: first.variant_id,
    Name: first.product_title,
    Brand: first.vendor || '',
    Price: format(first.price),
    CompareAtPrice: format(money(variant?.compareAtPrice) ? variant.compareAtPrice : first.price),
    Value: first.price / 100,
    URL: url.href,
    ImageURL: imageURL && ['https:', 'http:'].includes(imageURL.protocol) ? imageURL.href : '',
    Categories: Array.isArray(metadata?.categories) ? metadata.categories : [],
    Currency: cart.currency,
    currency: cart.currency,
    Country: country,
    AddedVia: kind,
    AddedItems: added.map((item, index) => ({ProductID: actual[index].product_id, VariantID: actual[index].variant_id, Name: actual[index].product_title, Quantity: item.quantity, Price: actual[index].price / 100})),
    $value: cart.total_price / 100,
    total_price: cart.total_price / 100,
    original_total_price: cart.original_total_price / 100,
    total_discount: cart.total_discount,
    item_count: cart.item_count,
    items
  };
}

export function createCartTracker(window, context = {}) {
  const document = window.document;
  if (!document || active.has(document)) return active.get(document);
  const seen = new WeakSet(), packIntents = new Set();
  const allowed = () => {
    try {
      const privacy = window.Shopify?.customerPrivacy;
      return !window.Shopify?.designMode && privacy?.marketingAllowed() === true && privacy?.analyticsProcessingAllowed() === true;
    } catch {return false;}
  };
  const send = receipt => {
    try {
      if (!allowed()) return false;
      const payload = cartEventPayload(receipt, {...context, origin: window.location.origin});
      if (!payload || !allowed()) return false;
      window._learnq = window._learnq || [];
      if (typeof window._learnq.push !== 'function') return false;
      window._learnq.push(['track', 'Added to Cart', payload]);
      return true;
    } catch {return false;}
  };
  const nativeAdd = event => {
    try {
      const detail = event.detail;
      if (!detail || seen.has(detail) || !Array.isArray(detail.items)) return;
      seen.add(detail);
      const quantityControl = event.target?.elements?.namedItem?.('quantity') || event.target?.querySelector?.('[name="quantity"]');
      const quantity = Number(quantityControl?.value || 1);
      send({cart: detail.cart, kind: 'product', additions: detail.items.map(item => ({productId: item.product_id, variantId: item.variant_id || item.id, quantity: detail.items.length === 1 && Number.isSafeInteger(quantity) && quantity > 0 ? quantity : 1}))});
    } catch {}
  };
  const packAdd = event => {
    try {
      const receipt = event.detail;
      if (!receipt?.intentId || packIntents.has(receipt.intentId)) return;
      packIntents.add(receipt.intentId);
      send(receipt);
    } catch {}
  };
  document.addEventListener('variant:add', nativeAdd);
  document.addEventListener('raith:pack-added', packAdd);
  if (!window.Shopify?.customerPrivacy && typeof window.Shopify?.loadFeatures === 'function') {
    try {window.Shopify.loadFeatures([{name: 'consent-tracking-api', version: '0.1'}], () => {});} catch {}
  }
  const tracker = {dispose() {document.removeEventListener('variant:add', nativeAdd); document.removeEventListener('raith:pack-added', packAdd); active.delete(document);}};
  active.set(document, tracker);
  return tracker;
}

if (typeof window !== 'undefined') {
  try {
    const context = JSON.parse(document.querySelector('[data-raith-klaviyo-context]')?.textContent || '{}');
    createCartTracker(window, context);
  } catch {}
}
